import { useEffect, useRef } from 'react'

/**
 * Repeatedly invoke an async function with a minimum time between executions.
 * If the async function takes longer than the minimum, the next invocation will
 * be after the async function executes.
 *
 * If the interval is changed, the next evocation will always occur after the
 * specificed interval, regardless of any current invocation.
 * @param seconds Minimum number of seconds between invocations
 * @param func Function to execute
 */
export const useAsyncInterval = (seconds: number | null, func: () => Awaitable<void>) => {
	const funcRef = useRef(func)
	const lastRunRef = useRef<number | null>(null)

	useEffect(() => {
		funcRef.current = func
	}, [func])

	useEffect(() => {
		if (seconds === null) {
			lastRunRef.current = null
			return
		}
		if (lastRunRef.current === null) {
			lastRunRef.current = Date.now()
		}
		let stopped = false
		let timeout: ReturnType<typeof setTimeout>

		const start = () => {
			const milliseconds = seconds * 1000 + lastRunRef.current! - Date.now()
			timeout = setTimeout(async () => {
				lastRunRef.current = Date.now()
				await funcRef.current()
				if (!stopped) {
					start()
				}
			}, milliseconds)
		}

		start()

		return () => {
			clearTimeout(timeout)
			stopped = true
		}
	}, [seconds])
}
